import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/feature/LoadingState";
import { supabase } from "@/lib/supabase";
import type { Customer, CustomerStatus, CustomerType } from "@/mocks/admin/customers";
import type { ProjectListItem, ProjectStatus } from "@/mocks/admin/projects";
import type { Booking, BookingStage } from "@/mocks/admin/bookings";
import type { Feedback } from "@/mocks/admin/feedback";
import ContactModal from "@/pages/project-detail/components/ContactModal";
import CustomerHero from "./components/CustomerHero";
import KpiRow from "./components/KpiRow";
import ContactPanel from "./components/ContactPanel";
import MoveHistory from "./components/MoveHistory";
import CustomerFeedback from "./components/CustomerFeedback";
import RecentInquiries from "./components/RecentInquiries";

function rowToCustomer(row: Record<string, unknown>): Customer {
  return {
    id: row.id as string,
    name: (row.name as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    type: (row.type as CustomerType) || "individual",
    status: (row.status as CustomerStatus) || "active",
    email: (row.email as string) || "",
    phone: (row.phone as string) || "",
    city: (row.city as string) || "",
    state: (row.state as string) || "",
    source: (row.source as string) || "",
    totalMoves: (row.total_moves as number) || 0,
    lifetimeValue: Number(row.lifetime_value) || 0,
    lastMoveDate: (row.last_move_date as string) || "",
    lastMoveRelative: (row.last_move_relative as string) || "",
    rating: row.rating === null ? null : (row.rating as number),
    tags: (row.tags as string[]) || [],
    nextBookingDate: (row.next_booking_date as string) || null,
  };
}

function rowToProject(row: Record<string, unknown>): ProjectListItem {
  return {
    id: row.id as string,
    customer: (row.customer_name as string) || "",
    customerInitials: (row.customer_initials as string) || "",
    customerColor: (row.customer_color as string) || "bg-ink-500",
    moveType: (row.move_type as string) || "",
    date: (row.move_date as string) || "",
    time: (row.move_time as string) || "",
    fromCity: (row.from_city as string) || "",
    toCity: (row.to_city as string) || "",
    truck: (row.truck as string) || "",
    crewCount: (row.crew_count as number) || 0,
    leadCrew: (row.lead_crew as string) || "",
    status: (row.status as ProjectStatus) || "scheduled",
    total: Number(row.total) || 0,
    depositPaid: !!row.deposit_paid,
  };
}

function rowToBooking(row: Record<string, unknown>): Booking {
  return {
    id: row.id as string,
    customer: (row.customer as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    email: (row.email as string) || "",
    phone: (row.phone as string) || "",
    moveType: (row.move_type as string) || "",
    preferredDate: (row.preferred_date as string) || "",
    preferredTime: (row.preferred_time as string) || "",
    fromCity: (row.from_city as string) || "",
    toCity: (row.to_city as string) || "",
    rooms: (row.rooms as string) || "",
    estimatedQuote:
      row.estimated_quote === null ? null : Number(row.estimated_quote),
    depositRequired:
      row.deposit_required === null ? null : Number(row.deposit_required),
    depositPaid: !!row.deposit_paid,
    stage: (row.stage as BookingStage) || "new",
    source: (row.source as string) || "",
    submittedAt: (row.submitted_at as string) || "",
    notes: (row.notes as string) || "",
  };
}

function rowToFeedback(row: Record<string, unknown>): Feedback {
  return {
    id: row.id as string,
    customer: (row.customer as string) || "",
    initials: (row.initials as string) || "",
    color: (row.color as string) || "bg-ink-500",
    projectId: (row.project_id as string) || "",
    moveType: (row.move_type as string) || "",
    rating: (row.rating as number) || 0,
    title: (row.title as string) || "",
    body: (row.body as string) || "",
    date: (row.date as string) || "",
    source: (row.source as Feedback["source"]) || "Email Survey",
    status: (row.status as Feedback["status"]) || "new",
    crewMentioned: (row.crew_mentioned as Feedback["crewMentioned"]) || [],
    reply: (row.reply as Feedback["reply"]) || undefined,
  };
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const { data: row, error: e1 } = await supabase
          .from("customers")
          .select("*")
          .eq("id", id)
          .maybeSingle();
        if (e1) throw e1;
        if (!row) {
          if (!cancelled) setError("Customer not found");
          return;
        }
        if (cancelled) return;
        const c = rowToCustomer(row);
        setCustomer(c);

        const [{ data: projectRows }, { data: bookingRows }, { data: feedbackRows }] =
          await Promise.all([
            supabase
              .from("moving_projects")
              .select("*")
              .eq("customer_name", c.name)
              .order("created_at", { ascending: false }),
            supabase
              .from("bookings")
              .select("*")
              .eq("customer", c.name)
              .order("created_at", { ascending: false }),
            supabase
              .from("feedback")
              .select("*")
              .eq("customer", c.name)
              .order("created_at", { ascending: false }),
          ]);

        if (cancelled) return;
        setProjects((projectRows || []).map(rowToProject));
        setBookings((bookingRows || []).map(rowToBooking));
        setFeedback((feedbackRows || []).map(rowToFeedback));
      } catch (e) {
        if (!cancelled)
          setError(e instanceof Error ? e.message : "Failed to load customer");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const upcomingProjects = useMemo(
    () =>
      projects.filter(
        (p) => p.status === "scheduled" || p.status === "in-progress",
      ),
    [projects],
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="px-4 md:px-8 py-6 md:py-8">
          <LoadingState message="Loading customer profile…" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !customer) {
    return (
      <AdminLayout>
        <div className="px-4 md:px-8 py-12 max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto rounded-full bg-rose-50 text-rose-500 flex items-center justify-center mb-3">
            <i className="ri-user-search-line text-2xl"></i>
          </div>
          <div className="font-display text-xl font-bold text-ink-900">
            Customer not found
          </div>
          <div className="text-sm text-ink-500 mt-1">
            {error || "We couldn't locate this customer record."}
          </div>
          <Link
            to="/customers"
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-md text-sm font-semibold bg-ink-900 text-white hover:bg-ink-800 cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Customers
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="px-4 md:px-8 py-6 md:py-8 max-w-[1400px] mx-auto">
        {/* Breadcrumb */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2 text-xs text-ink-500 min-w-0">
            <Link to="/dashboard" className="hover:text-ink-900 cursor-pointer whitespace-nowrap">
              Dashboard
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/customers" className="hover:text-ink-900 cursor-pointer whitespace-nowrap">
              Customers
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-ink-900 font-semibold truncate">{customer.name}</span>
          </div>
          <Link
            to="/customers"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </Link>
        </div>

        {/* 1. Identity */}
        <CustomerHero
          customer={customer}
          onEdit={() => {
            setToast("Edit panel coming soon");
            setTimeout(() => setToast(""), 2400);
          }}
          onMessage={() => setContactOpen(true)}
          onNewProject={() => navigate("/projects")}
        />

        {/* 2. KPIs */}
        <div className="mt-4">
          <KpiRow customer={customer} upcomingCount={upcomingProjects.length} />
        </div>

        {/* 3. Content — clean 2-column split */}
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-4">
            <MoveHistory projects={projects} loading={false} />
            <CustomerFeedback feedback={feedback} />
          </div>

          <div className="space-y-4">
            <ContactPanel customer={customer} />
            <RecentInquiries bookings={bookings} />
          </div>
        </div>
      </div>

      <ContactModal
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        audience="customer"
        recipient={{
          name: customer.name,
          email: customer.email,
          phone: customer.phone,
        }}
        projectId={projects[0]?.id || customer.id}
        moveType={projects[0]?.moveType || "General"}
        date={projects[0]?.date || customer.lastMoveDate}
      />

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-ink-900 text-white text-sm font-semibold px-4 py-3 rounded-md flex items-center gap-2">
          <i className="ri-information-line text-brand-300"></i>
          {toast}
        </div>
      )}
    </AdminLayout>
  );
}